import React from "react";
import Banner from "./Banner";
import { Breadcrumbs } from "./Breadcrumbs";
import { DynamicBreadcrumbs } from "./DynamicBreadcrumbs";
import SidebarNav from "./SidebarNav";
import type { NavigationItem } from "./static-section/static";
import type { MenuId } from "@/lib/enum";

export type PageLayoutProps = {
  title: string;
  bannerUrl?: string;
  children: React.ReactNode;
  navigation?: NavigationItem[];
  useDynamicBreadcrumbs?: boolean;
  /**
   * Optional: Show sidebar navigation for a specific menu
   * Example: menuId={MenuId.Services} or menuId={MenuId.AboutUs}
   */
  sidebarMenuId?: MenuId;
  /**
   * Optional fallback for dynamic pages when MenuId cannot be derived.
   * SidebarNav will use this slug to resolve parent node.
   */
  sidebarParentSlug?: string;
};

export default function PageLayout({
  title,
  bannerUrl,
  children,
  navigation,
  useDynamicBreadcrumbs = false,
  sidebarMenuId,
  sidebarParentSlug,
}: PageLayoutProps) {
  // Use DynamicBreadcrumbs if navigation data is provided, otherwise use regular Breadcrumbs
  const breadcrumbComponent = useDynamicBreadcrumbs && navigation ? (
    <DynamicBreadcrumbs navigation={navigation} currentTitle={title} />
  ) : (
    <Breadcrumbs />
  );

  return (
    <div
      className="bg-no-repeat bg-bottom-right"
      style={{
        backgroundImage: `url('/images/flower-supergrafis.webp')`,
        backgroundSize: '400px',
      }}
    >
      <Banner title={title} breadcrumb={breadcrumbComponent} bannerUrl={bannerUrl} />

      {/* Konten */}
      <main className="py-8">
        <div className="flex items-start gap-8 container mx-auto max-w-7xl px-10">
          {/* Main Content */}
          <div className="flex-1 min-w-0">{children}</div>

          {/* Sidebar Navigation */}
          {(sidebarMenuId || sidebarParentSlug) && (
            <aside className="w-80 shrink-0 sticky top-24 self-start hidden lg:block sm:hidden">
              <SidebarNav menuId={sidebarMenuId} parentSlug={sidebarParentSlug} />
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}